import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ICEScore from './ICEScore';
import DecisionReason from './DecisionReason';
import { useEPMContext } from '../../../../contexts/EPMContext';
import { formatTestCardId } from '../../../../utils/converter';
import { iceScore as testIceScore, testId } from '../../epmHelpers';

// Starting point for a test that hasn't been scored yet (priority 0/0/0)
const UNSCORED_DEFAULTS = { impact: 5, confidence: 5, ease: 5 };

const toScores = (priority) => {
    const scored = priority && (priority.impact || priority.confidence || priority.ease);
    const source = scored ? priority : UNSCORED_DEFAULTS;
    return { Impact: source.impact, Confidence: source.confidence, Ease: source.ease };
};

const PriorityStep = ({ testData, setTestData }) => {
    const navigate = useNavigate();
    const { tests, tagsMap } = useEPMContext();
    const [viableOpen, setViableOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [rankOpen, setRankOpen] = useState(false);

    const [scores, setScores] = useState(() => toScores(testData?.priority));

    useEffect(() => {
        setScores(toScores(testData?.priority));
    }, [testData?.priority]);

    // Unscored tests carry the defaults into the draft, so "Accept" saves a real score
    useEffect(() => {
        if (testData && testIceScore(testData) === null) {
            setTestData((prev) => ({ ...prev, priority: { ...UNSCORED_DEFAULTS } }));
        }
    }, [testData, setTestData]);

    const iceScore = useMemo(
        () => (Object.values(scores).reduce((sum, v) => sum + Number(v || 0), 0) / 3).toFixed(1),
        [scores],
    );

    // Where this test would sit in the Prioritised backlog, by ICE
    const ranking = useMemo(() => {
        const thisId = String(testId(testData));
        const others = (tests || [])
            .filter((test) => test.status === 'PRIORITISED' && String(testId(test)) !== thisId)
            .map((test) => ({ test, ice: testIceScore(test) ?? 0 }));
        const rows = [...others, { test: testData, ice: Number(iceScore), isThis: true }]
            .sort((a, b) => b.ice - a.ice);
        const position = rows.findIndex((row) => row.isThis);
        return { rows, position, next: rows[position + 1] };
    }, [tests, testData, iceScore]);

    // Win rate for each tag on this test: tests with that tag whose verdict is
    // Win, over all tests with that tag
    const tagWinRates = useMemo(() => {
        const tagIdOf = (tag) => String(tag?._id || tag);
        const tags = new Map();
        (testData?.tag_ids || []).forEach((tag) => {
            const tagId = tagIdOf(tag);
            if (!tags.has(tagId)) tags.set(tagId, tagsMap?.[tagId]?.name || tag?.name || tagId);
        });
        return [...tags].map(([tagId, name]) => {
            const tagged = (tests || []).filter((test) => (test.tag_ids || []).some((tag) => tagIdOf(tag) === tagId));
            const wins = tagged.filter((test) => String(test?.verdict?.status || '').toLowerCase() === 'win').length;
            const total = tagged.length;
            return { tagId, name, wins, total, rate: total ? Math.round((wins / total) * 100) : 0 };
        });
    }, [tests, tagsMap, testData?.tag_ids]);

    const historySummary = tagWinRates.length
        ? tagWinRates.slice(0, 2).map(({ name, rate }) => `${name} wins ${rate}%`).join(' · ')
        : 'No tags on this test yet';

    const rankLabel = ranking.position === 0 ? 'Top of Prioritised' : `#${ranking.position + 1} in Prioritised`;
    const defaultReason = `${rankLabel} at ${iceScore} and viable in ~16 days`;

    return (
        <div className="epm-priority">
            <header className="epm-workflow-step__heading epm-priority__heading">
                <span>02</span>
                <div><h2>Priority</h2><p>Score the idea consistently and decide whether it should move into planning.</p></div>
            </header>

            <ICEScore
                iceScore={iceScore}
                scores={scores}
                setScores={setScores}
                setTestData={setTestData}
                testData={testData}
            />

            <div className="epm-priority__summaries">
                {/* Viable — hidden for now
                <div className="epm-priority__summary-wrap relative">
                    <button className="epm-priority__summary cursor-pointer" type="button" onClick={() => setViableOpen(!viableOpen)}>
                        <span className="epm-priority__status" />
                        <strong>Viable</strong><span>~16 days at 5% minimum worthwhile effect</span><em>Adobe Analytics · detail</em>
                    </button>
                    {viableOpen && (
                        <div className="px-6 pb-6 pt-1">
                            <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2.5 text-[13px] text-[#5e6881] max-w-[420px]">
                                <span>Daily eligible visitors</span><b className="text-[#334064] text-right">17,600</b>
                                <span>Baseline conversion</span><b className="text-[#334064] text-right">11.2%</b>
                                <span>Minimum worthwhile effect (org setting)</span><b className="text-[#334064] text-right">5%</b>
                                <span>Estimated duration</span><b className="text-[#334064] text-right">16 days</b>
                            </div>
                            <div className="text-[10.5px] font-bold tracking-[.1em] uppercase text-[#8d96ac] mt-7 mb-3">Bands</div>
                            <div className="flex flex-wrap gap-2.5 text-[11.5px]">
                                <span className="border border-[#334064] bg-[#334064] text-white rounded-full px-2.5 py-1">≤ 4 weeks · viable</span>
                                <span className="border border-[#dce1ee] bg-white text-[#5e6881] rounded-full px-2.5 py-1">4–8 weeks · slow</span>
                                <span className="border border-[#dce1ee] bg-white text-[#5e6881] rounded-full px-2.5 py-1">8–12 weeks · marginal, Impact ≥ 8 only</span>
                                <span className="border border-dashed border-[#dce1ee] bg-white text-[#5e6881] rounded-full px-2.5 py-1">&gt; 12 weeks · not viable</span>
                            </div>
                            <p className="text-[12px] text-[#8992a8] mt-5 mb-0">Source: <span className="font-mono">adobe-analytics</span> adapter · location <span className="font-mono">/loja/telemoveis/*</span> · trailing 4 weeks. Computed, not entered. Without an adapter the figures are typed in and the result is marked <span className="border border-[#dce1ee] bg-white text-[#5e6881] rounded-full px-2 py-[2px] text-[11px] ml-1">unverified</span>.</p>
                        </div>
                    )}
                </div>
                */}

                <div className="epm-priority__summary-wrap relative">
                    <button className="epm-priority__summary cursor-pointer" type="button" onClick={() => setHistoryOpen(!historyOpen)}>
                        <ChevronRight size={18} className={historyOpen ? 'rotate-90 transition-transform' : 'transition-transform'} />

                        <strong>Your history</strong><span>{historySummary}</span><em>{historyOpen ? 'Hide' : 'Show'}</em>
                    </button>
                    {historyOpen && (
                        <div className="px-6 pb-6 pt-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                <div>

                                    <div className="text-[10.5px] font-bold tracking-[.1em] uppercase text-[#8d96ac] mb-3">Win rate by change type</div>
                                    {/* <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2.5 text-[13px] text-[#5e6881]">
                                        <span>Layout</span><b className="text-[#334064] text-right">31%</b>
                                        <span>New feature</span><b className="text-[#334064] text-right">22%</b>
                                        <span>Copy</span><b className="text-[#334064] text-right">8%</b>
                                    </div> */}
                                    {tagWinRates.length ? (
                                        <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2.5 text-[13px] text-[#5e6881]">
                                            {tagWinRates.map(({ tagId, name, wins, total, rate }) => (
                                                <React.Fragment key={tagId}>
                                                    <span className="font-mono">{name}</span>
                                                    <b className="text-[#334064] text-right">{rate}% <span className="text-[#8992a8] font-normal text-[12px] ml-1">{wins}/{total}</span></b>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="m-0 text-[13px] text-[#8992a8]">Add tags to this test in the Idea step to see their win rates.</p>
                                    )}
                                </div>
                                {/* Effect sizes — hidden for now
                                <div>
                                    <div className="text-[10.5px] font-bold tracking-[.1em] uppercase text-[#8d96ac] mb-3">Effect sizes</div>
                                    <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2.5 text-[13px] text-[#5e6881]">
                                        <span>Median winning uplift</span><b className="text-[#334064] text-right">+2.1%</b>
                                        <span>90th percentile</span><b className="text-[#334064] text-right">+5.4%</b>
                                        <span>Planned effect, median</span><b className="text-[#334064] text-right">+8.0%</b>
                                    </div>
                                    <div className="border border-[#edf0f6] bg-[#fafbff] rounded-lg mt-6 p-4 text-[13px] text-[#5e6881]">Teams here plan for 8% and win at 2%.</div>
                                </div>
                                */}
                            </div>
                        </div>
                    )}
                </div>

                <div className="epm-priority__summary-wrap relative">
                    <button className="epm-priority__summary cursor-pointer" type="button" onClick={() => setRankOpen(!rankOpen)}>
                        <ChevronRight size={18} className={rankOpen ? 'rotate-90 transition-transform' : 'transition-transform'} />
                        <strong>Where this ranks</strong>
                        <span>
                            #{ranking.position + 1} of {ranking.rows.length} in Prioritised
                            {ranking.next && ` · next is ${formatTestCardId(ranking.next.test)} at ${ranking.next.ice.toFixed(1)}`}
                        </span>
                        <em>{rankOpen ? 'Hide' : 'Show'}</em>
                    </button>
                    {rankOpen && (
                        <div className="px-6 pb-6 pt-1">
                            <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2.5 text-[13px] text-[#5e6881] max-w-[420px]">
                                {ranking.rows.slice(0, 5).map(({ test, ice, isThis }) => (
                                    <React.Fragment key={String(testId(test))}>
                                        <span className={isThis ? '' : 'text-[#8992a8]'}>
                                            {isThis ? <strong>{formatTestCardId(test)}</strong> : formatTestCardId(test)} {test?.name}
                                        </span>
                                        <b className={`text-right ${isThis ? 'text-[#334064]' : 'text-[#8992a8]'}`}>{ice.toFixed(1)}</b>
                                    </React.Fragment>
                                ))}
                            </div>
                            <div className="mt-5 flex">
                                <button type="button" onClick={() => navigate('/epm/testing-board')} className="border border-[#dce1ee] rounded-md px-4 py-2 text-[12px] font-semibold text-[#5e6881] bg-white shadow-sm hover:bg-gray-50 transition-colors">Open Prioritised column</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <DecisionReason
                testData={testData}
                setTestData={setTestData}
                defaultReason={defaultReason}
            />
        </div>
    );
};

export default PriorityStep;
